import { Fragment } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { AlgorithmStatusType } from '~/enums/AlgorithmStatusType';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';

import { OCRProcessingStatus } from '../types';

interface EditCommandPanelProps {
  editMode: boolean;
  isSaving: boolean;
  canEdit: boolean;
  documentId: number;
  dateModified: string;
  OCRProcessing?: OCRProcessingStatus;
  onDiscard: () => void;
  onSave: () => void;
  onEditClick: () => void;
}

export const EditCommandPanel = ({
  editMode,
  isSaving,
  canEdit,
  documentId,
  dateModified,
  OCRProcessing,
  onDiscard,
  onSave,
  onEditClick,
}: EditCommandPanelProps) => {
  const intl = useIntl();
  const formatDate = useFormatDate();

  if (
    OCRProcessing &&
    OCRProcessing.status === AlgorithmStatusType.InProgress
  ) {
    return (
      <StatusMessage
        message={intl.formatMessage({
          id: 'DocumentOverviewPanel.processingMessage',
          defaultMessage:
            'Evisort AI is processing this document. It will be available for editing in a few minutes.',
          description: 'Message shown when document is being processed',
        })}
        action={
          canEdit
            ? {
                text: intl.formatMessage({
                  id: 'DocumentOverviewPanel.editButton',
                  defaultMessage: 'Edit',
                  description: 'Edit button text',
                }),
                disabled: true,
                icon: 'edit',
              }
            : undefined
        }
      />
    );
  }

  return (
    <Box
      as="section"
      border="border"
      borderRadius="m"
      bg={editMode ? 'status.active.light' : 'background.quiet'}
      p={4}
    >
      <Layout align="center" spacing={4}>
        {editMode && canEdit ? (
          <Fragment>
            <Box flex="1">
              {intl.formatMessage({
                id: 'EditCommandPanel.editingMessage',
                defaultMessage: 'You are currently editing this document.',
                description: 'Message shown when user is in edit mode',
              })}
            </Box>
            <Layout spacing={4}>
              <Button
                text={intl.formatMessage({
                  id: 'EditCommandPanel.discardButton',
                  defaultMessage: 'Discard',
                  description: 'Discard changes button text',
                })}
                onClick={onDiscard}
                disabled={isSaving}
              />
              <Button
                text={intl.formatMessage({
                  id: 'EditCommandPanel.saveChangesButton',
                  defaultMessage: 'Save Changes',
                  description: 'Save changes button text',
                })}
                onClick={onSave}
                isLoading={isSaving}
                variant="primary"
              />
            </Layout>
          </Fragment>
        ) : (
          <Fragment>
            <Box flex="1">
              <FormattedMessage
                id="EditCommandPanel.lastEditedMessage"
                defaultMessage="This document was last edited on <strong>{editDate}.</strong>"
                description="Message showing when document was last edited. Example: editDate = 'January 15, 2024'"
                values={{
                  strong: (chunks) => <strong>{chunks}</strong>,
                  editDate: formatDate(dateModified, 'long'),
                }}
              />
            </Box>
            {canEdit && (
              <Button
                id={`document_overviewPanel_editButton?document=${documentId}`}
                label="edit-document-overview-button"
                text={intl.formatMessage({
                  id: 'EditCommandPanel.editButtonText',
                  defaultMessage: 'Edit',
                  description: 'Edit button text',
                })}
                icon="edit"
                onClick={onEditClick}
              />
            )}
          </Fragment>
        )}
      </Layout>
    </Box>
  );
};
